import type { ChangeEvent } from 'react';
import React, { memo, useLayoutEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { css } from '@compiled/react';

import Checkbox from '@atlaskit/checkbox';
import { token } from '@atlaskit/tokens';

import type { TaskAction } from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider.ts';
import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { getLogger } from '@confluence/logger';

import { useTaskRowsContext } from './TaskRowsContext.tsx';
import { CellContainerCompiled } from './presentationalComponents.tsx';
import { ColumnWidths } from './tasksDefinitions.ts';
import { useRefHeight } from './useRefHeight.ts';

interface DescriptionProps {
	adf?: string | null; // The ADF of the task to render
	rowId: string; // The identifier for the row to control styles as well as set height.
	id: string;
	onTaskCheckboxClick: (taskAction: TaskAction) => void;
	defaultChecked: boolean; // Because this is an uncontrolled component we just set the default.
	contentId: string; // The task's contentId
}

const checkboxContainerStyles = css({
	marginTop: token('space.050'),
});

const onChange = (
	event: ChangeEvent<HTMLInputElement>,
	rowID: string,
	callback: () => void = () => null,
) => {
	const currentRow = document.getElementById(rowID);
	const cols = currentRow?.getElementsByTagName('td');
	const isCompleted = event.target.checked;

	if (currentRow && cols) {
		currentRow.style.backgroundColor = isCompleted
			? token('color.background.success')
			: token('color.background.neutral.subtle');

		if (cols[0]) {
			cols[0].style.textDecoration = isCompleted ? 'line-through' : 'none';
		}
	}

	callback();
};

// The checkbox height is 24px while the description line height is 20px.

const descriptionContainerStyles = css({
	marginTop: token('space.025'),
});

const logger = getLogger('tasks-logger');

export const DescriptionColumn = memo(
	({ adf, rowId, id, onTaskCheckboxClick, defaultChecked, contentId }: DescriptionProps) => {
		const { setTaskRow } = useTaskRowsContext();
		const { initialHeight, ref } = useRefHeight();

		const document = useMemo(() => {
			try {
				return JSON.parse(adf ?? '');
			} catch {
				logger.error`Unable to parse ADF`;
				return null;
			}
		}, [adf]);

		useLayoutEffect(() => {
			if (Number.isInteger(initialHeight)) {
				setTaskRow(rowId, { rowHeight: initialHeight });
			}
		}, [initialHeight, rowId, setTaskRow]);

		return (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
			<CellContainerCompiled ref={ref} style={{ width: ColumnWidths.Description }}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives */}
				<div css={checkboxContainerStyles}>
					<Checkbox
						id={rowId}
						testId="description-task-checkbox"
						defaultChecked={defaultChecked}
						onChange={(event) => {
							const checked = event.target.checked;

							onChange(event, rowId, () =>
								onTaskCheckboxClick({
									objectKey: {
										localId: id,
										objectAri: id,
									},
									state: checked ? 'DONE' : 'TODO',
									contentId,
								}),
							);
						}}
					/>
				</div>
				<span css={descriptionContainerStyles}>
					{document ? (
						<ADFRenderer document={document} />
					) : (
						<FormattedMessage
							id="home.views.tasks.description.error"
							defaultMessage="Unable to load task description"
							description="A message displayed to users that a task description was not able to be loaded"
						/>
					)}
				</span>
			</CellContainerCompiled>
		);
	},
);
